// Routing rules editor for rules-mode integrations.
//
// Each rule pairs (field accessor + op + value) with a target service.
// First-match-wins, so order matters — admins can reorder via up/down
// buttons. Editing is local-only until "Save changes" is hit; the
// callable rebuilds the whole list on save (not partial-edit) which
// keeps the on-disk shape predictable.
//
// Field path conventions, surfaced via placeholder text per provider:
//   Datadog    tags.service        ("service:api-gateway" → "api-gateway")
//   New Relic  tags.service.name   (key-as-string, dotted)
//   Sentry     tags.project        (synthetic from data.issue.project.slug)
//   Generic    tags.<your_key>     (admin-supplied tag map)

import { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import type {
  IntegrationProvider,
  RoutingOp,
  RoutingRule,
  Service,
} from '../../lib/types';
import { Badge, Button, FormField, Input, Row, Stack } from '../ui';

const OPS: ReadonlyArray<{ value: RoutingOp; label: string; hint: string }> = [
  { value: 'equals', label: 'equals', hint: 'exact match' },
  { value: 'contains', label: 'contains', hint: 'substring' },
  { value: 'in', label: 'in', hint: 'comma-separated values' },
  { value: 'regex', label: 'regex', hint: 'regular expression' },
];

/** Mutable working copy of a rule — id is undefined for unsaved rows. */
export interface DraftRule {
  id?: string;
  field: string;
  op: RoutingOp;
  /** Stored as a single string in the editor; converted to string[] on save when op === 'in'. */
  value: string;
  serviceId: string;
}

export interface RoutingRulesEditorProps {
  provider: IntegrationProvider;
  services: Service[];
  initialRules: RoutingRule[];
  initialDefaultServiceId: string | undefined;
  onSave: (input: {
    rules: Array<{ id?: string; field: string; op: RoutingOp; value: string | string[]; serviceId: string }>;
    defaultServiceId: string | undefined;
  }) => Promise<void>;
}

export function RoutingRulesEditor({
  provider,
  services,
  initialRules,
  initialDefaultServiceId,
  onSave,
}: RoutingRulesEditorProps) {
  const [draft, setDraft] = useState<DraftRule[]>(() =>
    initialRules.map((r) => ({
      id: r.id,
      field: r.field,
      op: r.op,
      value: Array.isArray(r.value) ? r.value.join(', ') : r.value,
      serviceId: r.serviceId,
    })),
  );
  const [defaultServiceId, setDefaultServiceId] = useState<string>(
    initialDefaultServiceId ?? '',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  // Pristine when the draft matches what's saved. We compute this so
  // "Save changes" only enables when there's something to save.
  const dirty = useMemo(() => {
    if ((initialDefaultServiceId ?? '') !== defaultServiceId) return true;
    if (draft.length !== initialRules.length) return true;
    for (let i = 0; i < draft.length; i += 1) {
      const d = draft[i];
      const r = initialRules[i];
      const initialValue = Array.isArray(r.value) ? r.value.join(', ') : r.value;
      if (
        d.id !== r.id ||
        d.field !== r.field ||
        d.op !== r.op ||
        d.value !== initialValue ||
        d.serviceId !== r.serviceId
      ) {
        return true;
      }
    }
    return false;
  }, [draft, defaultServiceId, initialRules, initialDefaultServiceId]);

  const sortedServices = useMemo(
    () => [...services].sort((a, b) => a.name.localeCompare(b.name)),
    [services],
  );
  const fieldPlaceholder = fieldHintForProvider(provider);

  function updateRule(index: number, patch: Partial<DraftRule>) {
    setDraft((current) =>
      current.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    );
  }
  function addRule() {
    setDraft((current) => [
      ...current,
      { field: fieldPlaceholder, op: 'equals', value: '', serviceId: '' },
    ]);
  }
  function deleteRule(index: number) {
    setDraft((current) => current.filter((_, i) => i !== index));
  }
  function moveRule(index: number, direction: -1 | 1) {
    setDraft((current) => {
      const next = [...current];
      const target = index + direction;
      if (target < 0 || target >= next.length) return next;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  async function save() {
    setError(null);

    // Local validation before we hit the wire — same constraints the
    // server enforces, surfaced inline so the admin doesn't bounce off
    // the callable.
    for (const r of draft) {
      if (!r.field.trim()) {
        setError('Every rule needs a field path.');
        return;
      }
      if (!r.serviceId) {
        setError('Every rule needs a target service.');
        return;
      }
      if (r.op === 'in') {
        const items = r.value
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean);
        if (items.length === 0) {
          setError('"in" rules need at least one comma-separated value.');
          return;
        }
      } else if (!r.value.trim()) {
        setError(`Rule "${r.field}" needs a value.`);
        return;
      }
      if (r.op === 'regex') {
        try {
          new RegExp(r.value);
        } catch {
          setError(`"${r.value}" isn't a valid regex.`);
          return;
        }
      }
    }
    if (draft.length === 0 && !defaultServiceId) {
      setError(
        'A rules-mode integration needs at least one rule or a default service — otherwise every alert would be dropped.',
      );
      return;
    }

    setSaving(true);
    try {
      const rules = draft.map((r) => ({
        id: r.id,
        field: r.field.trim(),
        op: r.op,
        value:
          r.op === 'in'
            ? r.value.split(',').map((v) => v.trim()).filter(Boolean)
            : r.value.trim(),
        serviceId: r.serviceId,
      }));
      await onSave({ rules, defaultServiceId: defaultServiceId || undefined });
      setSavedFlash(true);
    } catch (err) {
      console.error('[routing] save failed', err);
      setError(extractMessage(err));
    } finally {
      setSaving(false);
    }
  }

  // Auto-clear the saved flash after a couple of seconds.
  useEffect(() => {
    if (!savedFlash) return;
    const id = setTimeout(() => setSavedFlash(false), 2000);
    return () => clearTimeout(id);
  }, [savedFlash]);

  return (
    <Stack gap={4}>
      <Stack gap={2}>
        <Row gap={2} justify="between" align="center">
          <span style={{ fontWeight: 'var(--fw-medium)' }}>Routing rules</span>
          <Button
            variant="secondary"
            size="sm"
            onClick={addRule}
            iconLeft={<Plus size={12} strokeWidth={2} />}
          >
            Add rule
          </Button>
        </Row>
        <span className="ds-text-muted ds-text-sm">
          Evaluated in order — first match wins. Inbound payloads are normalised so{' '}
          <code className="ds-mono">tags.&lt;key&gt;</code> works for every provider.
        </span>
      </Stack>

      {draft.length === 0 ? (
        <div
          style={{
            padding: 'var(--space-4)',
            background: 'var(--color-surface)',
            border: '1px dashed var(--color-border-strong)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-text-secondary)',
            fontSize: 'var(--fs-sm)',
          }}
        >
          No rules yet. {defaultServiceId
            ? 'Every alert will land on the default service below until you add some.'
            : 'You\'ll need at least one rule, or a default service, before this integration can route alerts.'}
        </div>
      ) : (
        <Stack gap={3}>
          {draft.map((rule, index) => (
            <RuleRow
              key={index}
              rule={rule}
              index={index}
              total={draft.length}
              services={sortedServices}
              fieldPlaceholder={fieldPlaceholder}
              onChange={(patch) => updateRule(index, patch)}
              onDelete={() => deleteRule(index)}
              onMove={(d) => moveRule(index, d)}
            />
          ))}
        </Stack>
      )}

      <FormField
        label="Default service"
        hint="Used when no rule matches. Leave blank to drop unmatched alerts."
      >
        {(id) => (
          <select
            id={id}
            className="ds-input"
            value={defaultServiceId}
            onChange={(e) => setDefaultServiceId(e.target.value)}
          >
            <option value="">No default — drop unmatched alerts</option>
            {sortedServices.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        )}
      </FormField>

      {error && (
        <div
          style={{
            padding: 'var(--space-3) var(--space-4)',
            background: 'var(--color-danger-soft)',
            color: 'var(--color-danger-text)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-danger)',
            fontSize: 'var(--fs-sm)',
          }}
        >
          {error}
        </div>
      )}

      <Row gap={2} justify="end" align="center">
        {savedFlash && (
          <Badge tone="success" dot>
            Saved
          </Badge>
        )}
        <Button
          variant="primary"
          onClick={save}
          loading={saving}
          disabled={!dirty}
        >
          Save changes
        </Button>
      </Row>
    </Stack>
  );
}

function RuleRow({
  rule,
  index,
  total,
  services,
  fieldPlaceholder,
  onChange,
  onDelete,
  onMove,
}: {
  rule: DraftRule;
  index: number;
  total: number;
  services: Service[];
  fieldPlaceholder: string;
  onChange: (patch: Partial<DraftRule>) => void;
  onDelete: () => void;
  onMove: (direction: -1 | 1) => void;
}) {
  return (
    <div
      style={{
        padding: 'var(--space-3) var(--space-4)',
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      <Stack gap={3}>
        <Row gap={2} align="center" justify="between">
          <Row gap={1} align="center">
            <span
              className="ds-mono ds-text-tertiary ds-text-xs"
              style={{ minWidth: 16 }}
            >
              {index + 1}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onMove(-1)}
              disabled={index === 0}
              aria-label="Move rule up"
            >
              <ArrowUp size={12} strokeWidth={2} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onMove(1)}
              disabled={index === total - 1}
              aria-label="Move rule down"
            >
              <ArrowDown size={12} strokeWidth={2} />
            </Button>
          </Row>
          <Button
            variant="danger-ghost"
            size="sm"
            iconLeft={<Trash2 size={12} strokeWidth={2} />}
            onClick={onDelete}
          >
            Remove
          </Button>
        </Row>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 2fr) minmax(0, 2fr)',
            gap: 'var(--space-3)',
          }}
        >
          <FormField label="When field" hint={`e.g. ${fieldPlaceholder}`}>
            {(id) => (
              <Input
                id={id}
                type="text"
                value={rule.field}
                onChange={(e) => onChange({ field: e.target.value })}
                placeholder={fieldPlaceholder}
              />
            )}
          </FormField>

          <FormField label="op">
            {(id) => (
              <select
                id={id}
                className="ds-input"
                value={rule.op}
                onChange={(e) => onChange({ op: e.target.value as RoutingOp })}
              >
                {OPS.map((o) => (
                  <option key={o.value} value={o.value} title={o.hint}>
                    {o.label}
                  </option>
                ))}
              </select>
            )}
          </FormField>

          <FormField
            label="value"
            hint={rule.op === 'in' ? 'comma-separated' : undefined}
          >
            {(id) => (
              <Input
                id={id}
                type="text"
                value={rule.value}
                onChange={(e) => onChange({ value: e.target.value })}
                placeholder={
                  rule.op === 'in'
                    ? 'api, auth, billing'
                    : rule.op === 'regex'
                      ? '^api-.+$'
                      : 'api-gateway'
                }
              />
            )}
          </FormField>

          <FormField label="route to">
            {(id) => (
              <select
                id={id}
                className="ds-input"
                value={rule.serviceId}
                onChange={(e) => onChange({ serviceId: e.target.value })}
              >
                <option value="">Pick a service…</option>
                {services.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            )}
          </FormField>
        </div>
      </Stack>
    </div>
  );
}

function fieldHintForProvider(provider: IntegrationProvider): string {
  switch (provider) {
    case 'datadog':
      return 'tags.service';
    case 'newrelic':
      return 'tags.service.name';
    case 'sentry':
      return 'tags.project';
    case 'dynatrace':
      return 'tags.service';
    case 'pagerduty':
      return 'tags.service';
    case 'generic':
      return 'tags.service';
  }
}

function extractMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message: unknown }).message;
    if (typeof m === 'string' && m.length > 0) return m;
  }
  return 'Save failed. Try again.';
}
